/**
 * useAuth hook - Authentication state and operations
 * @implements TS-045 - GraphQL Auth Operations and Hooks
 * @implements FS-001 - User Authentication
 * @implements FS-003 - GraphQL API Architecture
 * @implements FS-003.5.2 - WebSocket Authentication (stores sessionToken)
 *
 * This hook provides:
 * - Current user state from useMeQuery
 * - Login/logout functions
 * - Loading and error states
 * - Refetch capability for auth state refresh
 *
 * Note: OAuth login still uses REST endpoints (redirects to provider).
 * Password login uses the GraphQL Login mutation.
 */

import { useCallback, useMemo, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';

/**
 * localStorage key for WebSocket auth token
 * @implements FS-003.5.2 - WebSocket Authentication
 */
export const AUTH_TOKEN_KEY = 'authToken';
import {
  useMeQuery,
  useLoginMutation,
  useLogoutMutation,
  useRegisterProductOwnerMutation,
  User,
  LoginInput,
  RegisterPoInput,
} from '@/generated/graphql';

/**
 * OAuth provider types supported by the platform
 * @implements FS-001.2.1 - OAuth Authentication
 */
export type OAuthProvider = 'github' | 'google';

/**
 * Auth flow type for OAuth redirects
 */
export type AuthFlow = 'login' | 'register_po';

/**
 * Error type for auth operations
 */
export interface AuthError {
  message: string;
  code?: string;
}

/**
 * Return type for the useAuth hook
 */
export interface UseAuthResult {
  /** Current authenticated user or null */
  user: User | null;
  /** Whether the initial auth check is loading */
  isLoading: boolean;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Error from the Me query */
  error: AuthError | null;

  /** Login with email and password */
  loginWithPassword: (input: LoginInput) => Promise<void>;
  /** Login mutation loading state */
  isLoggingIn: boolean;
  /** Login mutation error */
  loginError: AuthError | null;

  /** Logout the current user */
  logout: () => Promise<void>;
  /** Logout mutation loading state */
  isLoggingOut: boolean;

  /** Register as a Product Owner */
  registerProductOwner: (input: RegisterPoInput) => Promise<string | null>;
  /** Registration loading state */
  isRegistering: boolean;
  /** Registration error */
  registerError: AuthError | null;

  /** Redirect to OAuth provider for login */
  loginWithOAuth: (provider: OAuthProvider) => void;
  /** Redirect to OAuth provider for PO registration */
  registerPOWithOAuth: (
    provider: OAuthProvider,
    invitationCode?: string,
  ) => void;

  /** Refetch the current user (useful after auth state changes) */
  refetch: () => Promise<void>;
}

/**
 * Extract error from Apollo error
 */
function extractError(error: unknown): AuthError | null {
  if (!error) return null;

  if (error instanceof Error) {
    // Check for GraphQL error extensions
    const apolloError = error as {
      graphQLErrors?: Array<{
        message: string;
        extensions?: { code?: string };
      }>;
    };
    if (apolloError.graphQLErrors && apolloError.graphQLErrors.length > 0) {
      const gqlError = apolloError.graphQLErrors[0];
      return {
        message: gqlError.message,
        code: gqlError.extensions?.code,
      };
    }
    return { message: error.message };
  }

  return { message: 'An unknown error occurred' };
}

/**
 * Build OAuth redirect URL
 * @implements FS-001.2.1 - OAuth Authentication
 */
function buildOAuthUrl(
  provider: OAuthProvider,
  flow: AuthFlow,
  params?: Record<string, string>,
): string {
  const baseUrl = `/auth/${provider}`;
  const searchParams = new URLSearchParams({ flow, ...params });
  return `${baseUrl}?${searchParams.toString()}`;
}

/**
 * useAuth hook - the source of truth for authentication state
 *
 * @example
 * const { user, isAuthenticated, login, logout, isLoading } = useAuth();
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (!isAuthenticated) return <LoginForm onSubmit={login} />;
 * return <Dashboard user={user} onLogout={logout} />;
 */
export function useAuth(): UseAuthResult {
  const apolloClient = useApolloClient();

  // Query current user - this is the source of truth for auth state
  const {
    data: meData,
    loading: meLoading,
    error: meError,
    refetch: refetchMe,
  } = useMeQuery({
    // Don't throw on error - handle gracefully
    errorPolicy: 'all',
    // Fetch on mount to check auth state
    fetchPolicy: 'network-only',
  });

  // Login mutation
  const [loginMutation, { loading: loginLoading, error: loginMutationError }] =
    useLoginMutation();

  // Logout mutation
  const [logoutMutation, { loading: logoutLoading }] = useLogoutMutation();

  // Registration mutations
  const [
    registerPOMutation,
    { loading: registerPOLoading, error: registerPOError },
  ] = useRegisterProductOwnerMutation();

  // Derived state
  const user = meData?.me ?? null;
  const isAuthenticated = !!user;
  const isLoading = meLoading;
  const error = extractError(meError);

  /**
   * Check for ws_token cookie from OAuth callbacks and store in localStorage.
   * OAuth callbacks set this cookie so the frontend can access the token
   * for WebSocket authentication.
   * @implements FS-003.5.2 - WebSocket Authentication
   */
  useEffect(() => {
    // Parse cookies to find ws_token
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=');
      if (key && value) {
        acc[key] = decodeURIComponent(value);
      }
      return acc;
    }, {} as Record<string, string>);

    const wsToken = cookies['ws_token'];
    if (wsToken) {
      // Store in localStorage for WebSocket connectionParams
      localStorage.setItem(AUTH_TOKEN_KEY, wsToken);

      // Delete the cookie by setting expired date
      document.cookie =
        'ws_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      console.log('[Auth] Stored session token from OAuth callback');
    }
  }, []); // Run once on mount

  /**
   * Login with email and password
   * @implements FS-001.2.2 - Password Authentication (Backup)
   * @implements FS-003.5.2 - Stores sessionToken for WebSocket auth
   */
  const loginWithPassword = useCallback(
    async (input: LoginInput): Promise<void> => {
      const result = await loginMutation({
        variables: { input },
      });

      // Check for GraphQL errors and throw them for the caller to catch
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data?.login) {
        // No data and no errors - unexpected state
        throw new Error('Login failed. Please try again.');
      }

      // Store session token for WebSocket authentication
      // @implements FS-003.5.2 - WebSocket Authentication
      if (result.data.login.sessionToken) {
        localStorage.setItem(AUTH_TOKEN_KEY, result.data.login.sessionToken);
      }

      // Refetch user to update cache
      await refetchMe();
    },
    [loginMutation, refetchMe],
  );

  /**
   * Logout the current user
   * @implements FS-003.5.2 - Clears sessionToken from localStorage
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      await logoutMutation();
    } finally {
      // Clear session token used for WebSocket auth
      localStorage.removeItem(AUTH_TOKEN_KEY);
      // Clear Apollo cache to remove all user data
      await apolloClient.clearStore();
      // Redirect to home
      window.location.href = '/';
    }
  }, [logoutMutation, apolloClient]);

  /**
   * Register as a Product Owner
   * @implements FS-001.1.1 - Product Owner Registration
   * @implements FS-003.5.2 - Stores sessionToken for WebSocket auth
   * @returns The redirect URL for project creation, or null on error
   */
  const registerProductOwner = useCallback(
    async (input: RegisterPoInput): Promise<string | null> => {
      const result = await registerPOMutation({
        variables: { input },
      });

      if (result.data?.registerProductOwner) {
        // Store session token for WebSocket authentication
        // @implements FS-003.5.2 - WebSocket Authentication
        if (result.data.registerProductOwner.sessionToken) {
          localStorage.setItem(
            AUTH_TOKEN_KEY,
            result.data.registerProductOwner.sessionToken,
          );
        }
        await refetchMe();
        return result.data.registerProductOwner.redirectTo;
      }
      return null;
    },
    [registerPOMutation, refetchMe],
  );

  /**
   * Redirect to OAuth provider for login
   * @implements FS-001.2.1 - OAuth Authentication
   */
  const loginWithOAuth = useCallback((provider: OAuthProvider): void => {
    window.location.href = buildOAuthUrl(provider, 'login');
  }, []);

  /**
   * Redirect to OAuth provider for Product Owner registration
   * @implements FS-001.1.1 - Product Owner Registration
   * @implements BS-001.1 - Invitation Code Management
   */
  const registerPOWithOAuth = useCallback(
    (provider: OAuthProvider, invitationCode?: string): void => {
      const params = invitationCode ? { invitationCode } : undefined;
      window.location.href = buildOAuthUrl(provider, 'register_po', params);
    },
    [],
  );

  /**
   * Refetch the current user
   */
  const refetch = useCallback(async (): Promise<void> => {
    await refetchMe();
  }, [refetchMe]);

  // Memoize the result to prevent unnecessary re-renders
  return useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated,
      error,

      loginWithPassword,
      isLoggingIn: loginLoading,
      loginError: extractError(loginMutationError),

      logout,
      isLoggingOut: logoutLoading,

      registerProductOwner,
      isRegistering: registerPOLoading,
      registerError: extractError(registerPOError),

      loginWithOAuth,
      registerPOWithOAuth,

      refetch,
    }),
    [
      user,
      isLoading,
      isAuthenticated,
      error,
      loginWithPassword,
      loginLoading,
      loginMutationError,
      logout,
      logoutLoading,
      registerProductOwner,
      registerPOLoading,
      registerPOError,
      loginWithOAuth,
      registerPOWithOAuth,
      refetch,
    ],
  );
}
